import { differenceInDays, subDays } from 'date-fns';
import { BillingModel } from '@/lib/billing/types';
import { BILLING_MODEL_CONFIGS, DEFAULT_BILLING_MODEL } from '@/lib/billing/configs';

export interface ChurnRiskFactors {
  sessionFrequencyDecline: boolean;
  missedSessionsIncreasing: boolean;
  packageEndingSoon: boolean;
  noRenewalScheduled: boolean;
  longGapSinceLastSession: boolean;
  streakBroken: boolean;
  lowEngagement: boolean;
  resultsStalling: boolean;
}

export interface ChurnRiskScore {
  score: number; // 0-100, higher = more at risk
  level: 'low' | 'medium' | 'high' | 'critical';
  factors: ChurnRiskFactors;
  primaryReason: string;
  recommendations: string[];
  /** Array of factor keys that triggered — used for persisted `reason_tags` column. */
  reasonTags: (keyof ChurnRiskFactors)[];
}

export interface ClientEngagementData {
  clientId: string;
  sessions: Array<{
    date: string;
    cancelled: boolean;
    completed_at: string | null;
  }>;
  activePackage: {
    size: number;
    sessions_completed: number;
    activated_at: string | null;
  } | null;
  nextPackageDate: string | null;
  streak: {
    current_streak: number;
    longest_streak: number;
    last_workout_date: string | null;
    total_workouts: number;
  } | null;
  progressData?: {
    hasPlateaus: boolean;
    plateauCount: number;
    maxWeeksSinceProgress: number;
    complianceTrend: 'improving' | 'declining' | 'stable' | null;
    complianceScore: number | null;
    recentMeasurementCount: number; // measurements logged in last 30 days
  };
}

const WEIGHTS = {
  sessionFrequencyDecline: 18,
  missedSessionsIncreasing: 22,
  packageEndingSoon: 13,
  noRenewalScheduled: 18,
  longGapSinceLastSession: 13,
  streakBroken: 6,
  lowEngagement: 10,
  resultsStalling: 20,
};

export function analyzeChurnRisk(
  data: ClientEngagementData,
  billingModel: BillingModel = DEFAULT_BILLING_MODEL
): ChurnRiskScore {
  const rawFactors = detectRiskFactors(data);
  const factors = filterFactorsByBillingModel(rawFactors, billingModel);
  const score = calculateScore(factors, billingModel);
  const level = determineLevel(score);
  const primaryReason = getPrimaryReason(factors);
  const recommendations = generateRecommendations(factors, data);
  const reasonTags = (Object.keys(factors) as (keyof ChurnRiskFactors)[])
    .filter((k) => factors[k] === true);

  return { score, level, factors, primaryReason, recommendations, reasonTags };
}

/**
 * Filter out churn risk factors that don't apply to the current billing model.
 * For example, package-related factors don't apply to subscription or pay-per-session models.
 */
function filterFactorsByBillingModel(
  factors: ChurnRiskFactors,
  billingModel: BillingModel
): ChurnRiskFactors {
  const config = BILLING_MODEL_CONFIGS[billingModel];
  const churnConfig = config.churnRiskFactors;

  return {
    ...factors,
    // Zero out factors that don't apply to this billing model
    packageEndingSoon: churnConfig.packageEndingSoon ? factors.packageEndingSoon : false,
    noRenewalScheduled: churnConfig.noRenewalScheduled ? factors.noRenewalScheduled : false,
  };
}

function detectRiskFactors(data: ClientEngagementData): ChurnRiskFactors {
  const now = new Date();
  const { sessions, activePackage, nextPackageDate, streak } = data;

  // Filter to completed sessions only for frequency analysis
  const completedSessions = sessions
    .filter(s => !s.cancelled && s.completed_at)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Session frequency decline: compare last 30 days vs previous 30 days
  const last30Days = completedSessions.filter(s => 
    differenceInDays(now, new Date(s.date)) <= 30
  ).length;
  const prev30Days = completedSessions.filter(s => {
    const daysDiff = differenceInDays(now, new Date(s.date));
    return daysDiff > 30 && daysDiff <= 60;
  }).length;
  const sessionFrequencyDecline = prev30Days > 0 && last30Days < prev30Days * 0.7;

  // Missed sessions increasing: compare cancellation rates
  const recentSessions = sessions.filter(s => 
    differenceInDays(now, new Date(s.date)) <= 30
  );
  const olderSessions = sessions.filter(s => {
    const daysDiff = differenceInDays(now, new Date(s.date));
    return daysDiff > 30 && daysDiff <= 60;
  });
  const recentCancelRate = recentSessions.length > 0 
    ? recentSessions.filter(s => s.cancelled).length / recentSessions.length 
    : 0;
  const olderCancelRate = olderSessions.length > 0 
    ? olderSessions.filter(s => s.cancelled).length / olderSessions.length 
    : 0;
  const missedSessionsIncreasing = recentCancelRate > 0.3 || 
    (olderCancelRate > 0 && recentCancelRate > olderCancelRate * 1.5);

  // Package ending soon: less than 3 sessions remaining
  const packageEndingSoon = activePackage 
    ? (activePackage.size - activePackage.sessions_completed) <= 3 
    : false;

  // No renewal scheduled when package is ending
  const noRenewalScheduled = packageEndingSoon && !nextPackageDate;

  // Long gap since last session: more than 14 days
  const lastSessionDate = completedSessions[0]?.date;
  const longGapSinceLastSession = lastSessionDate 
    ? differenceInDays(now, new Date(lastSessionDate)) > 14 
    : sessions.length > 0; // Has sessions but none completed recently

  // Streak broken: had a streak but lost it
  const streakBroken = streak 
    ? streak.longest_streak >= 3 && streak.current_streak === 0 
    : false;

  // Low engagement: few total workouts relative to time as client
  const lowEngagement = streak
    ? streak.total_workouts < 5 && completedSessions.length > 10
    : false;

  // Results stalling: client isn't seeing progress (plateaus + declining compliance)
  const progressData = data.progressData;
  let resultsStalling = false;
  if (progressData) {
    const hasSignificantPlateau = progressData.hasPlateaus && progressData.maxWeeksSinceProgress >= 3;
    const complianceDeclining = progressData.complianceTrend === 'declining';
    const noMeasurements = progressData.recentMeasurementCount === 0;

    // Plateau + declining compliance = strongest signal
    if (hasSignificantPlateau && complianceDeclining) {
      resultsStalling = true;
    }
    // Plateau alone (moderate+) is still a risk signal
    else if (hasSignificantPlateau && progressData.maxWeeksSinceProgress >= 5) {
      resultsStalling = true;
    }
    // Compliance declining with no measurements = disengaging from tracking
    else if (complianceDeclining && noMeasurements) {
      resultsStalling = true;
    }
  }

  return {
    sessionFrequencyDecline,
    missedSessionsIncreasing,
    packageEndingSoon,
    noRenewalScheduled,
    longGapSinceLastSession,
    streakBroken,
    lowEngagement,
    resultsStalling,
  };
}

function calculateScore(factors: ChurnRiskFactors, billingModel: BillingModel): number {
  const config = BILLING_MODEL_CONFIGS[billingModel];
  const churnConfig = config.churnRiskFactors;

  // Get the applicable weights for this billing model
  const applicableWeights: Record<string, number> = {
    sessionFrequencyDecline: WEIGHTS.sessionFrequencyDecline,
    missedSessionsIncreasing: WEIGHTS.missedSessionsIncreasing,
    longGapSinceLastSession: WEIGHTS.longGapSinceLastSession,
    streakBroken: WEIGHTS.streakBroken,
    lowEngagement: WEIGHTS.lowEngagement,
    resultsStalling: WEIGHTS.resultsStalling,
  };

  // Only include package-related weights if they apply to this model
  if (churnConfig.packageEndingSoon) {
    applicableWeights.packageEndingSoon = WEIGHTS.packageEndingSoon;
  }
  if (churnConfig.noRenewalScheduled) {
    applicableWeights.noRenewalScheduled = WEIGHTS.noRenewalScheduled;
  }

  // Calculate max possible score for normalization
  const maxPossibleScore = Object.values(applicableWeights).reduce((a, b) => a + b, 0);

  // Calculate actual score
  let rawScore = 0;
  if (factors.sessionFrequencyDecline) rawScore += applicableWeights.sessionFrequencyDecline || 0;
  if (factors.missedSessionsIncreasing) rawScore += applicableWeights.missedSessionsIncreasing || 0;
  if (factors.packageEndingSoon) rawScore += applicableWeights.packageEndingSoon || 0;
  if (factors.noRenewalScheduled) rawScore += applicableWeights.noRenewalScheduled || 0;
  if (factors.longGapSinceLastSession) rawScore += applicableWeights.longGapSinceLastSession || 0;
  if (factors.streakBroken) rawScore += applicableWeights.streakBroken || 0;
  if (factors.lowEngagement) rawScore += applicableWeights.lowEngagement || 0;
  if (factors.resultsStalling) rawScore += applicableWeights.resultsStalling || 0;

  // Normalize to 0-100 scale based on maximum possible score for this model
  // This ensures non-package models can still reach high risk levels
  const normalizedScore = maxPossibleScore > 0
    ? Math.round((rawScore / maxPossibleScore) * 100)
    : 0;

  return Math.min(100, normalizedScore);
}

function determineLevel(score: number): ChurnRiskScore['level'] {
  if (score >= 70) return 'critical';
  if (score >= 50) return 'high';
  if (score >= 25) return 'medium';
  return 'low';
}

function getPrimaryReason(factors: ChurnRiskFactors): string {
  if (factors.noRenewalScheduled && factors.packageEndingSoon) {
    return 'Package running out and no renewal booked';
  }
  if (factors.missedSessionsIncreasing) {
    return 'Cancelling more sessions than usual';
  }
  if (factors.longGapSinceLastSession) {
    return "Haven't seen them in a while";
  }
  if (factors.sessionFrequencyDecline) {
    return 'Coming in less often lately';
  }
  if (factors.resultsStalling) {
    return 'Progress has stalled — not seeing results';
  }
  if (factors.streakBroken) {
    return 'Lost their workout momentum';
  }
  if (factors.lowEngagement) {
    return 'Not staying engaged with their training';
  }
  if (factors.packageEndingSoon) {
    return 'Package almost finished';
  }
  return 'Doing well!';
}

function generateRecommendations(factors: ChurnRiskFactors, data: ClientEngagementData): string[] {
  const recommendations: string[] = [];

  if (factors.noRenewalScheduled && factors.packageEndingSoon) {
    recommendations.push('Give them a call to chat about continuing');
    recommendations.push('Maybe offer a loyalty discount on their next package');
  }

  if (factors.missedSessionsIncreasing) {
    recommendations.push('Check if their schedule has changed');
    recommendations.push('See if different session times would work better');
  }

  if (factors.longGapSinceLastSession) {
    recommendations.push('Send a friendly text to see how they\'re doing');
    recommendations.push('Offer to schedule a catch-up session');
  }

  if (factors.sessionFrequencyDecline) {
    recommendations.push('Revisit their goals together');
    recommendations.push('Ask what\'s been getting in the way');
  }

  if (factors.streakBroken) {
    recommendations.push('Celebrate what they achieved before');
    recommendations.push('Help them set a fresh, easy goal to restart');
  }

  if (factors.resultsStalling) {
    recommendations.push('Schedule a progress review to reassess their program');
    recommendations.push('Introduce new exercises or rep schemes to break through the plateau');
    recommendations.push('Celebrate non-scale wins — energy, sleep, mood improvements');
  }

  if (factors.lowEngagement) {
    recommendations.push('Mix things up with new exercises');
    recommendations.push('Set up some fun milestones to hit');
  }

  return recommendations.slice(0, 3);
}

export function getRiskColor(level: ChurnRiskScore['level']): string {
  switch (level) {
    case 'critical': return 'text-destructive';
    case 'high': return 'text-warning';
    case 'medium': return 'text-warning';
    case 'low': return 'text-success';
  }
}

export function getRiskBgColor(level: ChurnRiskScore['level']): string {
  switch (level) {
    case 'critical': return 'bg-destructive/10 border-destructive/20';
    case 'high': return 'bg-warning/10 border-warning/20';
    case 'medium': return 'bg-warning/10 border-warning/20';
    case 'low': return 'bg-success/10 border-success/20';
  }
}
